// Profile screen - Player profile with photo, stats, and achievements
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCompetition } from '../contexts/CompetitionContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components';
import ImageCropModal from '../components/ImageCropModal';
import ProgressTimeline from '../components/ProgressTimeline';
import { uploadProfilePhoto, getPlayerAchievements, linkAnonymousToEmail, resendVerificationEmail } from '../services';
import { getOverallLeaderboard, getCategoryLeaderboard, calculatePlayerTimeline } from '../utils/leaderboard';

const Profile = () => {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const { competition, currentPlayer, players, categories, games, results } = useCompetition();
  const { user } = useAuth();

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [achievements, setAchievements] = useState([]);
  const [loadingAchievements, setLoadingAchievements] = useState(true);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Email linking state
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [linkingEmail, setLinkingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  // Load player achievements
  useEffect(() => {
    const loadAchievements = async () => {
      if (!currentPlayer) return;

      try {
        setLoadingAchievements(true);
        const playerAchievements = await getPlayerAchievements(currentPlayer.id);
        setAchievements(playerAchievements);
      } catch (error) {
        console.error('Error loading achievements:', error);
      } finally {
        setLoadingAchievements(false);
      }
    };

    loadAchievements();
  }, [currentPlayer]);

  // Calculate current competition stats
  const currentStats = useMemo(() => {
    if (!currentPlayer || !players.length) return null;

    const overallLeaderboard = getOverallLeaderboard(players, games, results, categories, competition?.rankingMethod || 'byCategory');
    const playerRank = overallLeaderboard.findIndex(p => p.id === currentPlayer.id) + 1;
    const playerData = overallLeaderboard.find(p => p.id === currentPlayer.id);

    // Category rankings
    const categoryRankings = categories
      .filter(cat => cat.status === 'active')
      .map(category => {
        const leaderboard = getCategoryLeaderboard(players, games, results, category.id);
        const rank = leaderboard.findIndex(p => p.id === currentPlayer.id) + 1;
        const playerCategoryData = leaderboard.find(p => p.id === currentPlayer.id);

        return {
          category,
          rank: rank > 0 ? rank : null,
          points: playerCategoryData?.totalPoints || 0,
          gamesPlayed: playerCategoryData?.gamesPlayed || 0
        };
      })
      .filter(cat => cat.gamesPlayed > 0);

    return {
      overallRank: playerRank > 0 ? playerRank : null,
      totalPoints: playerData?.totalPoints || 0,
      gamesPlayed: playerData?.gamesPlayed || 0,
      averagePoints: playerData?.averagePoints || 0,
      categoryRankings
    };
  }, [currentPlayer, players, games, results, categories, competition]);

  // Calculate timeline data
  const timelineData = useMemo(() => {
    if (!currentPlayer || !players.length) return [];
    return calculatePlayerTimeline(currentPlayer.id, players, games, results, categories, competition?.rankingMethod || 'byCategory');
  }, [currentPlayer, players, games, results, categories, competition]);

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async (croppedImageBlob) => {
    setUploadError('');
    setUploading(true);

    try {
      // Convert blob to file
      const file = new File([croppedImageBlob], 'profile.jpg', { type: 'image/jpeg' });
      await uploadProfilePhoto(currentPlayer.id, file);
      // Refresh competition data to get updated photo URL
      window.location.reload();
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleLinkEmail = async (e) => {
    e.preventDefault();
    setEmailError('');
    setEmailSuccess('');
    setLinkingEmail(true);

    try {
      await linkAnonymousToEmail(email, password);
      setEmailSuccess('Email linked successfully! A verification email has been sent.');
      setShowEmailForm(false);
      setEmail('');
      setPassword('');
      // Reload to show updated email
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      // If email already in use, show a more helpful error
      if (error.message.includes('already registered') || error.message.includes('already in use')) {
        setEmailError('This email is already registered. If this is your email, you may need to delete the old account first or contact the organizer for assistance.');
      } else {
        setEmailError(error.message);
      }
    } finally {
      setLinkingEmail(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      await resendVerificationEmail();
      setEmailSuccess('Verification email sent!');
    } catch (error) {
      setEmailError(error.message);
    }
  };

  if (!currentPlayer) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate(`/competition/${competitionId}`)}
            className="text-blue-600 hover:text-blue-700 mb-4 text-sm"
          >
            ← Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Profile Photo & Basic Info */}
        <Card className="mb-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Photo */}
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden mb-4">
                {currentPlayer.photoURL ? (
                  <img
                    src={currentPlayer.photoURL}
                    alt={currentPlayer.displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-5xl text-gray-400">
                    {currentPlayer.displayName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handlePhotoSelect}
                  className="hidden"
                  disabled={uploading}
                />
                <span className="text-sm text-blue-600 hover:text-blue-700">
                  {uploading ? 'Uploading...' : currentPlayer.photoURL ? 'Change Photo' : 'Upload Photo'}
                </span>
              </label>
              {uploadError && (
                <p className="text-xs text-red-500 mt-2 text-center">{uploadError}</p>
              )}
              <p className="text-xs text-gray-500 mt-2 text-center">
                Max 5MB • JPG, PNG, WebP
              </p>
            </div>

            {/* Basic Info */}
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">{currentPlayer.displayName}</h2>
              <p className="text-gray-600 mb-1">Competing in {competition.name}</p>
              {user && user.email ? (
                <div className="mb-3">
                  <p className="text-sm text-gray-500">
                    <span className="font-medium">Email:</span> {user.email}
                    {user.emailVerified ? (
                      <span className="ml-2 text-green-600 text-xs">✓ Verified</span>
                    ) : (
                      <span className="ml-2 text-orange-600 text-xs">⚠ Not verified</span>
                    )}
                  </p>
                  {!user.emailVerified && (
                    <button
                      onClick={handleResendVerification}
                      className="text-xs text-blue-600 hover:text-blue-700 underline mt-1"
                    >
                      Resend verification email
                    </button>
                  )}
                </div>
              ) : (
                <div className="mb-3">
                  <p className="text-sm text-gray-400 italic">
                    Anonymous account - no email linked
                  </p>
                  <button
                    onClick={() => setShowEmailForm(!showEmailForm)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium mt-1"
                  >
                    {showEmailForm ? 'Cancel' : '+ Link Email Address'}
                  </button>
                </div>
              )}
              {currentPlayer.isOrganizer && (
                <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  Organizer
                </span>
              )}
            </div>
          </div>
        </Card>

        {/* Email Linking Form */}
        {showEmailForm && (
          <Card className="mb-6">
            <h3 className="text-lg font-semibold mb-4">Link Email Address</h3>
            {emailError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {emailError}
              </div>
            )}
            {emailSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                {emailSuccess}
              </div>
            )}
            <form onSubmit={handleLinkEmail} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="your.email@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password (min 6 characters)
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Create a password"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={linkingEmail}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {linkingEmail ? 'Linking...' : 'Link Email'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailForm(false);
                    setEmailError('');
                    setEmailSuccess('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
              <p className="text-xs text-gray-500">
                You'll receive a verification email after linking. Your account will remain accessible even if you don't verify immediately.
              </p>
            </form>
          </Card>
        )}

        {/* Current Competition Stats */}
        {currentStats && (
          <Card title="Current Competition Stats" className="mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">
                  {currentStats.overallRank ? `#${currentStats.overallRank}` : '-'}
                </div>
                <div className="text-sm text-gray-600 mt-1">Overall Rank</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-3xl font-bold text-gray-900">{currentStats.totalPoints}</div>
                <div className="text-sm text-gray-600 mt-1">Total Points</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-3xl font-bold text-gray-900">{currentStats.gamesPlayed}</div>
                <div className="text-sm text-gray-600 mt-1">Games Played</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-3xl font-bold text-gray-900">
                  {currentStats.averagePoints.toFixed(1)}
                </div>
                <div className="text-sm text-gray-600 mt-1">Avg Points</div>
              </div>
            </div>

            {/* Category Rankings */}
            {currentStats.categoryRankings.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Category Rankings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {currentStats.categoryRankings.map(cat => (
                    <div
                      key={cat.category.id}
                      className="p-4 rounded-lg border-2"
                      style={{
                        backgroundColor: `${cat.category.color}15`,
                        borderColor: `${cat.category.color}40`
                      }}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-2xl">{cat.category.icon}</span>
                        <span className="font-semibold text-gray-900">{cat.category.name}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white bg-opacity-60 p-3 rounded text-center">
                          <div className="text-2xl font-bold" style={{ color: cat.category.color }}>
                            {cat.rank ? `#${cat.rank}` : '-'}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">Position</div>
                        </div>
                        <div className="bg-white bg-opacity-60 p-3 rounded text-center">
                          <div className="text-2xl font-bold text-gray-900">
                            {cat.points}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">Points</div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-2 text-center">
                        {cat.gamesPlayed} {cat.gamesPlayed === 1 ? 'game' : 'games'} played
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Progress Timeline */}
        {timelineData.length > 0 && (
          <Card title="Competition Progress" className="mb-6">
            <ProgressTimeline
              timelineData={timelineData}
              categories={categories.filter(c => c.status === 'active')}
            />
          </Card>
        )}

        {/* Previous Records / Achievements */}
        <Card title="Previous Records & Achievements">
          {loadingAchievements ? (
            <div className="text-center py-8 text-gray-500">Loading achievements...</div>
          ) : achievements.length > 0 ? (
            <div className="space-y-3">
              {achievements.map(achievement => (
                <div
                  key={achievement.id}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg border-2 border-yellow-300"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">
                      {achievement.type === 'overall_winner' ? '🏆' : '🥇'}
                    </div>
                    <div>
                      <div className="font-semibold text-lg">
                        {achievement.type === 'overall_winner'
                          ? 'Overall Winner'
                          : `${achievement.categoryName} Winner`}
                      </div>
                      <div className="text-sm text-gray-600">
                        {achievement.competitionName} • {achievement.year}
                      </div>
                      {achievement.notes && (
                        <div className="text-xs text-gray-500 mt-1">{achievement.notes}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-yellow-600">{achievement.year}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">🏅</div>
              <p className="text-gray-500">No achievements yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Win competitions to earn achievements!
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Image Crop Modal */}
      {selectedImage && (
        <ImageCropModal
          isOpen={showCropModal}
          onClose={() => {
            setShowCropModal(false);
            setSelectedImage(null);
          }}
          imageSrc={selectedImage}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
};

export default Profile;
